// controls/datetime.js — champ de sélection de date et heure
import React, { useState, useEffect } from "react";
import CommonControls from "./inc/Common";

/**
 * Création d’un champ datetime conforme JSON Schema draft 2020-12
 */
export function createDatetimeField() {
  return {
    type: "string",
    format: "date-time",
    title: "Date et heure",
    "x-ui": "datetime",
    "x-placeholder": "",
    "x-min": "",
    "x-max": "",
    "x-format": "YYYY-MM-DDTHH:mm", // format ISO local
    "x-defaultNow": false,
  };
}

/**
 * Éditeur du champ Datetime (FieldEditor)
 */
export function EditDatetimeField({ field, onChange }) {
  const [min, setMin] = useState(field["x-min"] || "");
  const [max, setMax] = useState(field["x-max"] || "");
  const [placeholder, setPlaceholder] = useState(field["x-placeholder"] || "");
  const [format, setFormat] = useState(field["x-format"] || "YYYY-MM-DDTHH:mm");
  const [defaultNow, setDefaultNow] = useState(field["x-defaultNow"] || false);

  // 🔄 Synchronisation vers le parent
  useEffect(() => onChange("x-min", min), [min]);
  useEffect(() => onChange("x-max", max), [max]);
  useEffect(() => onChange("x-placeholder", placeholder), [placeholder]);
  useEffect(() => onChange("x-format", format), [format]);
  useEffect(() => onChange("x-defaultNow", defaultNow), [defaultNow]);

  return (
    <>
      <CommonControls field={field} onChange={onChange} />

      {/* ---- Placeholder ---- */}
      <div className="mb-3">
        <label className="form-label">Texte indicatif (placeholder)</label>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="ex : Sélectionnez la date et l'heure"
          value={placeholder}
          onChange={(e) => setPlaceholder(e.target.value)}
        />
      </div>

      {/* ---- DateTime min / max ---- */}
      <div className="row mb-3">
        <div className="col">
          <label className="form-label">Date et heure minimales</label>
          <input
            type="datetime-local"
            className="form-control form-control-sm"
            value={min}
            onChange={(e) => setMin(e.target.value)}
          />
        </div>
        <div className="col">
          <label className="form-label">Date et heure maximales</label>
          <input
            type="datetime-local"
            className="form-control form-control-sm"
            value={max}
            onChange={(e) => setMax(e.target.value)}
          />
        </div>
      </div>

      {/* ---- Format ---- */}
      <div className="mb-3">
        <label className="form-label">Format de sortie</label>
        <select
          className="form-select form-select-sm"
          value={format}
          onChange={(e) => setFormat(e.target.value)}
        >
          <option value="YYYY-MM-DDTHH:mm">YYYY-MM-DDTHH:mm (ISO local)</option>
          <option value="YYYY-MM-DD HH:mm">YYYY-MM-DD HH:mm</option>
          <option value="DD/MM/YYYY HH:mm">DD/MM/YYYY HH:mm</option>
        </select>
      </div>

      {/* ---- Valeur par défaut ---- */}
      <div className="form-check mb-3">
        <input
          type="checkbox"
          className="form-check-input"
          id={`defaultNow_${field.id}`}
          checked={defaultNow}
          onChange={(e) => setDefaultNow(e.target.checked)}
        />
        <label className="form-check-label" htmlFor={`defaultNow_${field.id}`}>
          Définir la date et l'heure actuelles par défaut
        </label>
      </div>
    </>
  );
}

/* =========================================================
   🧩 FRONT — rendu du champ Datetime
   ========================================================= */
export const RenderDatetimeField = ({
  field,
  value,
  onChange,
  className,
  errorMessage   // <--- AJOUT
}) => {

  const placeholder = field["x-placeholder"] || "";
  const min = field["x-min"] || undefined;
  const max = field["x-max"] || undefined;
  const required = field.required || false;
  const defaultNow = field["x-defaultNow"] || false;

  // Si aucune valeur mais defaultNow activé → initialise à la date/heure actuelles
  const initialValue =
    value ||
    (defaultNow
      ? new Date().toISOString().slice(0, 16) // format YYYY-MM-DDTHH:mm
      : "");

  return (
    <div
      className={`
        fb-field fb-datetime
        ${field["x-bloc-class"] || ""}
        ${className || ""}
      `}
    >

      {/* Label */}
      {field.title && (
        <label className={`form-label ${errorMessage ? "text-danger" : ""}`}>
          {field.title}
        </label>
      )}

      {/* Input datetime-local */}
      <input
        type="datetime-local"
        className={`
          form-control
          ${field["x-control-class"] || ""}
          ${errorMessage ? "is-invalid" : ""}
        `}
        value={initialValue}
        min={min}
        max={max}
        placeholder={placeholder}
        onChange={(e) => onChange?.(e.target.value)}
      />

      {/* Message d’erreur */}
      {errorMessage && (
        <div className="invalid-feedback d-block mt-1">
          {errorMessage}
        </div>
      )}

    </div>
  );
};
